/**
 * ErrorToast Component - Auto-dismissing toast for retryable errors
 * Displays error message, Error ID, and optional retry button
 */

import React, { useEffect, useState } from "react";
import { ErrorSeverity } from "@akfdev-com/shared";

interface ErrorToastProps {
  message: string;
  errorId: string;
  severity?: ErrorSeverity;
  actions?: Array<{ label: string; onClick: () => void }>;
  onDismiss?: () => void;
  autoDismissMs?: number;
}

const severityStyles: Record<
  ErrorSeverity,
  { bg: string; icon: string; text: string }
> = {
  INFO: {
    bg: "bg-blue-50 border-blue-200",
    icon: "text-blue-600",
    text: "text-blue-800",
  },
  WARN: {
    bg: "bg-yellow-50 border-yellow-200",
    icon: "text-yellow-600",
    text: "text-yellow-800",
  },
  ERROR: {
    bg: "bg-red-50 border-red-200",
    icon: "text-red-600",
    text: "text-red-800",
  },
  CRITICAL: {
    bg: "bg-red-100 border-red-300",
    icon: "text-red-700",
    text: "text-red-900",
  },
};

/**
 * ErrorToast Component
 */
export const ErrorToast: React.FC<ErrorToastProps> = ({
  message,
  errorId,
  severity = "ERROR",
  actions = [],
  onDismiss,
  autoDismissMs = 8000,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Auto-dismiss after specified time
  useEffect(() => {
    if (autoDismissMs <= 0) return;

    const timer = setTimeout(() => {
      setIsVisible(false);
      onDismiss?.();
    }, autoDismissMs);

    return () => clearTimeout(timer);
  }, [autoDismissMs, onDismiss]);

  if (!isVisible) {
    return null;
  }

  const styles = severityStyles[severity];

  return (
    <div
      className={`fixed bottom-4 right-4 max-w-sm rounded-lg border p-4 shadow-lg ${styles.bg}`}
      role="alert"
      aria-live="polite"
      aria-label={`Error: ${message}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex flex-1 items-start">
          {/* Icon */}
          <div className={`mt-0.5 flex-shrink-0 ${styles.icon}`}>
            <svg
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          {/* Content */}
          <div className="ml-3 flex-1">
            <p className={`text-sm font-medium ${styles.text}`}>{message}</p>
            <p className={`mt-1 text-xs ${styles.text}`}>
              Error ID: <code className="font-mono">{errorId}</code>
            </p>
          </div>
        </div>

        {/* Close button */}
        <button
          onClick={() => {
            setIsVisible(false);
            onDismiss?.();
          }}
          className={`ml-3 flex-shrink-0 ${styles.icon} hover:opacity-75`}
          aria-label="Close error toast"
        >
          <svg
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>

      {/* Actions */}
      {actions.length > 0 && (
        <div className="mt-4 flex gap-2">
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={() => {
                action.onClick();
                setIsVisible(false);
              }}
              className={`rounded px-3 py-2 text-sm font-medium ${
                severity === "ERROR" || severity === "CRITICAL"
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-yellow-600 text-white hover:bg-yellow-700"
              }`}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

ErrorToast.displayName = "ErrorToast";
